import {
  createUserWithEmailAndPassword,
  updateProfile,
  User,
} from "firebase/auth";
import {
  initFirebase,
  clearFirebaseLocalStorage,
} from "@core/services/api/firebase";
import { apiRequest } from "@core/services/api/apiRequest";
import type { ClaimedVia } from "@core/features/invitation";

type Args = {
  data: any;
  inviteCode: string | null;
  claimedVia?: ClaimedVia | null;
  onError?: (error: Error) => void;
};

export async function registerUser({
  data,
  inviteCode,
  claimedVia,
  onError,
}: Args) {
  let firebaseUser: User | null = null;

  try {
    // Prevalidate: clean up orphaned Firebase accounts, check if email exists
    try {
      const { exists, retry_after } = await apiRequest(
        "/auth/prevalidate/",
        "POST",
        { email: data.email },
        false
      );
      if (!exists && retry_after) {
        throw new Error("Please wait a few minutes and try again.");
      }
      if (exists) {
        throw new Error("Email address already in use. Please try to log in.");
      }
    } catch (prevalidateError) {
      // If the email is already in use, surface that error immediately.
      // For any other prevalidate failure, proceed anyway (graceful degradation).
      if (
        (prevalidateError as Error).message?.includes("already in use") ||
        (prevalidateError as Error).message?.includes("Please wait")
      ) {
        throw prevalidateError;
      }
    }

    // Ensure firebase is initialized
    const auth = await initFirebase();

    // Create user in Firebase
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      data.email,
      data.password
    );

    firebaseUser = userCredential.user;

    // Update display name
    await updateProfile(firebaseUser, {
      displayName: `${data.firstName} ${data.lastName}`,
    });

    // Get ID token
    const idToken = await firebaseUser.getIdToken();

    // Prepare data for API call
    const body = {
      firebase_token: idToken,
      email: data.email,
      first_name: data.firstName,
      last_name: data.lastName,
      is_new_firebase_user: true,
      ...(inviteCode ? { invite_code: inviteCode } : {}),
      ...(claimedVia ? { claimed_via: claimedVia } : {}),
      ...(data.phoneNumber ? { phone_number: data.phoneNumber } : {}),
    };

    // Send data to backend
    const response = await apiRequest("/invitation/signup/", "POST", body);

    return { authToken: idToken, ...response };
  } catch (error) {
    // Backend handles Firebase user cleanup via Admin SDK on failure.
    // Just clear local state.
    clearFirebaseLocalStorage();

    onError?.(error as Error);

    throw error;
  }
}
